/**
 * Custom Lexical node for blurb embed elements
*/

import { yupResolver } from '@hookform/resolvers/yup';
import { $createParagraphNode, $getSelection, $isRangeSelection, type ElementFormatType, type LexicalNode, type NodeKey } from 'lexical';
import React from 'react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import * as yup from "yup";

import { Button } from '@cms/components/data/Button';
import { SystemDialogContent } from '@cms/components/data/Dialog';
import { TStoryContentElementTypeCustomEmbed, TStoryContentElementTypeCustomEmbedRoundTableInterviewees } from '@dl/declarations';
import { MarkdownFormatter } from '@dl/ui/components/shared/MarkdownFormatter';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { ControlledSystemTextInput } from '../../data/TextInput';
import { CustomEmbedNode, SerializeEmbedNode } from './CustomEmbedNode';
import { CustomEmbedPluginActions, CustomEmbedPluginContainer } from './CustomEmbedPluginContainer';

const intervieweeSchema = yup.object().shape({
    id: yup.number().required('ID is required'),
    name: yup.string().required('Name is required'),
    photo: yup.string().url('Photo must be a valid URL').required('Photo URL is required'),
    description: yup.string().required('Description is required'),
})

const intervieweesSchema = yup.object().shape({
    interviewees: yup.array().of(intervieweeSchema)
        .min(1, 'At least one interviewee is required')
        .max(5, 'A maximum of 5 interviewees is allowed')
})

type TBaseType = TStoryContentElementTypeCustomEmbed;
type TExtendedType = TStoryContentElementTypeCustomEmbedRoundTableInterviewees;

interface EmbedRoundTableIntervieweeProps {
    config: TBaseType
}

export const EmbedRoundTableInterviewee: React.FC<EmbedRoundTableIntervieweeProps> = ({ config }) => {
    const { interviewees = [] } = (config as TExtendedType).embed.config

    return (
        <div className="my-5 space-y-6 lg:my-[30px]">
            {interviewees.map((interviewee, index) => (
                <div key={index} className="flex flex-col items-start gap-4 lg:flex-row lg:space-x-4">
                    <div className="flex shrink-0 items-center space-x-4 lg:h-48 lg:w-48">
                        <img
                            className="h-24 min-h-24 w-24 min-w-24 rounded-full object-cover lg:h-48 lg:w-48"
                            src={interviewee.photo}
                            alt={interviewee.name}
                            width={150}
                            height={150}
                        />

                        <div className="font-semibold lg:hidden">{interviewee.name}</div>
                    </div>
                    <div>
                        <p className="mb-1 hidden font-semibold lg:block">{interviewee.name}</p>
                        <MarkdownFormatter
                            text={interviewee.description}
                            className="article-paragraph line-clamp-12 mb-0 lg:line-clamp-6"
                        />
                    </div>
                </div>
            ))}
        </div>
    )
}

export const EditEmbedRoundTableInterviewee: React.FC<
    EmbedRoundTableIntervieweeProps & {
        handleSubmit?: (payload: TExtendedType) => void;
    }
> = ({ config, handleSubmit }) => {
    const usedConfig = config as TExtendedType;

    const [editor] = useLexicalComposerContext();

    const methods = useForm({
        resolver: yupResolver(intervieweesSchema),
        defaultValues: {
            interviewees: usedConfig.embed.config.interviewees || [{
                id: 1,
                name: '',
                photo: '',
                description: '',
            }]
        }
    })

    const { handleSubmit: handleFormSubmit, control } = methods;

    const { fields, append, remove } = useFieldArray({
        control,
        name: "interviewees",
    });

    const handleSubmitEdits = handleFormSubmit((data) => {
        editor.update(() => {
            const payload: TExtendedType = {
                ...usedConfig,
                embed: {
                    ...usedConfig.embed,
                    config: {
                        ...usedConfig.embed.config,
                        ...data
                    }
                }
            }

            if (handleSubmit) {
                handleSubmit(payload)
            }
            else {
                const selection = $getSelection();

                if ($isRangeSelection(selection) && selection.isCollapsed()) {
                    const node = $createRoundTableIntervieweeNode(payload as TStoryContentElementTypeCustomEmbed);
                    selection.insertNodes([node, $createParagraphNode()]);
                }
            }
        })
    })

    return (

        <CustomEmbedPluginContainer
            embedTitle='Embed/Embed RoundTable Interviewees'
            embedDescription={(
                <>
                    <b>Add interviewee profiles for each Roundtable session</b>; they`ll appear at the top of each article with their name, photo, and bio.
                </>
            )}
            instructionsScreenshotUrl='/images/embeds/round-table-interviewees.png'
            instructionsNotesComponent={<div />}
            embedActionComponent={(
                <FormProvider {...methods}>
                    <form className="flex flex-col gap-4 overflow-y-scroll max-h-60 mb-10">
                        {fields.map((field, index) => (
                            <div
                                key={field.id}
                                className='flex flex-col p-2 gap-2'
                            >
                                <ControlledSystemTextInput
                                    name={`interviewees.${index}.name`}
                                    placeholder='Name'
                                    inputSize='full'
                                />

                                <ControlledSystemTextInput
                                    name={`interviewees.${index}.photo`}
                                    placeholder='Photo URL'
                                    inputSize='full'
                                />

                                <ControlledSystemTextInput
                                    name={`interviewees.${index}.description`}
                                    placeholder='Description'
                                    inputSize='full'
                                    multiline
                                    rows={3}
                                />

                                <div
                                    className='self-end'
                                >
                                    <Button
                                        type='button'
                                        inputVariant='white_text_red_bg'
                                        onClick={() => remove(index)}
                                    >
                                        Remove Interviewee
                                    </Button>
                                </div>
                            </div>
                        ))}

                    </form>

                    <div
                        className='flex items-center justify-end gap-2'
                    >
                        <Button
                            type='button'
                            inputVariant='none'
                            onClick={() => append({
                                id: fields.length + 1,
                                name: '',
                                photo: '',
                                description: '',
                            })}
                        >
                            Add Interviewee
                        </Button>
                        <CustomEmbedPluginActions
                            handleSubmit={handleSubmitEdits}
                        />
                    </div>
                </FormProvider>
            )}
        />
    );
}

export const EditEmbedRoundTableIntervieweeDialog: React.FC<
    EmbedRoundTableIntervieweeProps
> = ({ config }) => {
    return (
        <SystemDialogContent
            dialogProps={{
                title: 'Embed/RoundTable Interviewees',
            }}
            // trigger={{
            //     component: (
            //         <FaUsers size={24} />
            //     )
            // }}
            content={{
                component: (
                    <EditEmbedRoundTableInterviewee config={config} />
                ),
            }}
            actionsProps={{
                hide: true
            }}
        />
    )
}

export class CustomRoundTableIntervieweeNode extends CustomEmbedNode {
    __config: TBaseType;

    constructor(
        config: TBaseType,
        format?: ElementFormatType,
        key?: NodeKey,
    ) {
        super(
            "Embed RoundTable Interviewees",
            <EmbedRoundTableInterviewee config={config} />,
            <EditEmbedRoundTableInterviewee config={config} handleSubmit={(payload) => { this.handleSubmit(payload) }} />,
            config,
            format,
            key
        );
        this.__config = config;
    }

    handleSubmit(config: TExtendedType) {
        this.setConfig(config as TBaseType);
    }

    static getType(): TStoryContentElementTypeCustomEmbedRoundTableInterviewees['subtype'] {
        return "roundtable-interviewees";
    }

    static clone(node: CustomRoundTableIntervieweeNode): CustomRoundTableIntervieweeNode {
        return new CustomRoundTableIntervieweeNode(node.__config, node.__format, node.__key);
    }

    static importJSON(serializedNode: SerializeEmbedNode): CustomRoundTableIntervieweeNode {
        return $createRoundTableIntervieweeNode(
            serializedNode.config,
        ).updateFromJSON(
            serializedNode,
        );
    }
}

export function $createRoundTableIntervieweeNode(
    config: TBaseType,
): CustomRoundTableIntervieweeNode {
    return new CustomRoundTableIntervieweeNode(config);
}

export function $isRoundTableIntervieweeNode(
    node: CustomRoundTableIntervieweeNode | LexicalNode | null | undefined,
): node is CustomRoundTableIntervieweeNode {
    return node instanceof CustomRoundTableIntervieweeNode;
}
